import api from './api';

/** Tranche C2/C3 (#489): Wissensbasis — Quellen, Suche, Lektions-Referenzen. */
export type KnowledgeScope = 'platform' | 'company';
export type KnowledgeLicense = 'official_work' | 'public_domain' | 'public_copyrighted' | 'licensed' | 'internal';
export type KnowledgeSourceStatus = 'pending' | 'processing' | 'ready' | 'failed';

export const KNOWLEDGE_LICENSES: KnowledgeLicense[] = ['internal', 'official_work', 'public_domain', 'public_copyrighted', 'licensed'];
export const KNOWLEDGE_JURISDICTIONS = ['DE', 'AT', 'CH', 'EU', 'INT'] as const;

export interface KnowledgeSource {
    id: number;
    scope: KnowledgeScope;
    companyId: number | null;
    title: string;
    publisher: string | null;
    documentNumber: string | null;
    jurisdiction: string;
    license: KnowledgeLicense;
    licenseLabel: string;
    sourceType: string;
    originUrl: string | null;
    originalFilename: string | null;
    archived: boolean;
    status: KnowledgeSourceStatus;
    errorMessage: string | null;
    chunkCount: number;
    characterCount: number;
    hasEmbeddings: boolean;
    version: number;
    validFrom: string | null;
    supersedesId: number | null;
    isCurrent: boolean;
    preview: string;
    createdBy: number | null;
    createdAt: string;
    ingestedAt: string | null;
    referenceCount?: number;
}

export interface KnowledgeSourceOption {
    id: number;
    title: string;
    documentNumber: string | null;
    publisher: string | null;
    scope: KnowledgeScope;
    license: KnowledgeLicense;
    own: boolean;
}

export interface KnowledgeHit {
    chunkId: number;
    sourceId: number;
    chunkIndex: number;
    content: string;
    headingPath: string | null;
    page: number | null;
    scope: KnowledgeScope;
    sourceTitle: string;
    publisher: string | null;
    documentNumber: string | null;
    license: KnowledgeLicense;
    citation: string;
    distance: number;
    score: number;
}

export interface LessonSourceReference {
    id: number;
    lessonId: number;
    lessonTitle: string;
    courseId: number;
    refLabel: string;
    citation: string;
    headingPath: string | null;
    page: number | null;
    usageNote: string | null;
    sourceId: number;
    sourceTitle: string;
    documentNumber: string | null;
    publisher: string | null;
    license: KnowledgeLicense;
    scope: KnowledgeScope;
    sourceIsCurrent: boolean;
}

export interface KnowledgeSourceMeta {
    title: string;
    license: KnowledgeLicense;
    publisher?: string;
    documentNumber?: string;
    jurisdiction?: string;
    validFrom?: string;
    supersedesId?: number | null;
}

export type KnowledgeSourceCreateInput = KnowledgeSourceMeta & ({ file: File } | { url: string } | { text: string });

const base = (scope: KnowledgeScope): string => (scope === 'platform' ? '/platform/knowledge' : '/knowledge');

export const knowledgeService = {
    async list(scope: KnowledgeScope = 'company'): Promise<{ sources: KnowledgeSource[]; platformCount?: number; embeddingsAvailable: boolean }> {
        const { data } = await api.get(`${base(scope)}/sources`);
        return data;
    },
    async options(): Promise<KnowledgeSourceOption[]> {
        const { data } = await api.get('/knowledge/sources/options');
        return data.options;
    },
    async create(input: KnowledgeSourceCreateInput, scope: KnowledgeScope = 'company'): Promise<KnowledgeSource> {
        if ('file' in input) {
            const form = new FormData();
            form.append('file', input.file);
            Object.entries(input).forEach(([key, value]) => {
                if (key !== 'file' && value !== undefined && value !== null && value !== '') form.append(key, String(value));
            });
            const { data } = await api.post(`${base(scope)}/sources`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
            return data;
        }
        const { data } = await api.post(`${base(scope)}/sources`, input);
        return data;
    },
    async remove(id: number, scope: KnowledgeScope = 'company'): Promise<void> {
        await api.delete(`${base(scope)}/sources/${id}`);
    },
    async reingest(id: number, scope: KnowledgeScope = 'company'): Promise<KnowledgeSource> {
        const { data } = await api.post(`${base(scope)}/sources/${id}/reingest`);
        return data;
    },
    async search(query: string, options: { limit?: number; sourceIds?: number[]; includePlatform?: boolean } = {}, scope: KnowledgeScope = 'company'): Promise<KnowledgeHit[]> {
        const { data } = await api.post(`${base(scope)}/search`, { query, ...options });
        return data.hits;
    },
    async lessonReferences(lessonId: number): Promise<LessonSourceReference[]> {
        const { data } = await api.get(`/ai/lessons/${lessonId}/source-references`);
        return data.references;
    },
    async courseReferences(courseId: number): Promise<{ lessons: { lessonId: number; lessonTitle: string; references: LessonSourceReference[] }[]; sourceCount: number }> {
        const { data } = await api.get(`/ai/courses/${courseId}/source-references`);
        return data;
    },
};

export default knowledgeService;
